// ─────────────────────────────────────────────────────────────────────
// WHICH CAPABILITY EACH /sa WRITE CONTROL ASKS FOR (#924).
//
// Every /sa section used to decide what to OFFER from one role NAME,
// `platformRoles.includes('PLATFORM_SUPERADMIN')`, while the API decides what
// to ALLOW from capabilities (`requireCapability(..., { scope: 'platform' })`).
// Roles are data: the permission matrix can move a platform capability to
// another role, and the panel could not see the move. Granting
// `rating.adjust_manual` to support left the correction screen read-only;
// taking a capability from the superadmin left a control that answered 403.
//
// So `/me` now reports the caller's platform capabilities, and each control is
// keyed on the capability ITS ROUTE requires. This table is that mapping, and
// nothing else: no React, no imports. That is so `apps/api/tests/
// sa-write-capabilities.test.ts` can import it and drive every entry against
// the real route guard, which is what catches a control keyed on the wrong
// capability.
//
// The panel still only decides what is OFFERED. Every write is authorized
// again by the server.
// ─────────────────────────────────────────────────────────────────────

export const SA_WRITE_CAPABILITIES = {
  /** «Rettigheter»: create, edit and delete roles, tick capabilities. */
  matrix: 'platform.roles.manage',
  /** «Organisasjoner»: create one, edit name/kind/payment account, link a club. */
  organizationSetup: 'platform.organizations.manage',
  /** «Organisasjoner»: grant, revoke and invite into an organization's roles. */
  organizationRoles: 'platform.roles.manage',
  /** «Organisasjoner»: «Jobb som» — start an audited impersonation. */
  impersonation: 'platform.impersonate_organization',
  /** «Brukere»: grant and revoke platform roles. */
  userRoles: 'platform.roles.manage',
  /** «Brukere»: disable or re-enable an account. */
  userAccounts: 'platform.accounts.manage',
  /** «Rating»: correct a player's rating. */
  ratingCorrection: 'rating.adjust_manual',
  /**
   * «Spillerprofil» (#796): support corrects a player's identity — the whole
   * SECTION, like «Rapporter», because the section is the edit.
   */
  playerSupportEdit: 'member.profile.support_edit',
  /** «Motor»: save the platform-wide engine settings. */
  engineSettings: 'engine.settings.manage',
  /**
   * «Revisjon» (#1041): the whole SECTION, not one control in it. The only
   * entry here that guards a READ — the audit trail is not «who holds what»
   * (`platform.directory.read`, which every other section reads behind), so
   * the menu entry is keyed on the capability `GET /platform/audit` asks for,
   * and is driven against that guard like every write above.
   */
  auditRead: 'platform.audit.read',
  /**
   * «Rapporter» (#1043): the whole SECTION, like «Revisjon». The platform's
   * figures — every organization's activity and the card money — are not
   * readable on a platform role alone, so the menu entry is keyed on the
   * capability `GET /platform/reports` asks for, and driven against it.
   */
  reportsRead: 'platform.reports.read',
  /**
   * «Betalingsavvik» (#1131): the whole SECTION, like «Rapporter». Money the
   * system could not settle, across every organization, is not readable on a
   * platform role alone, so the menu entry is keyed on the capability
   * `GET /platform/payment-exceptions` asks for, and driven against it.
   */
  paymentExceptionsRead: 'platform.payment_exceptions.read',
} as const;

export type SaWriteRight = keyof typeof SA_WRITE_CAPABILITIES;
export type SaWriteRights = Record<SaWriteRight, boolean>;

/**
 * What the panel may OFFER, from the capabilities the server reported.
 *
 * Absent (an older API that does not send the list) offers nothing. That fails
 * closed for at most one deploy, which is better than guessing from a role.
 */
export function saWriteRights(platformCapabilities: readonly string[] | undefined): SaWriteRights {
  const held = new Set(platformCapabilities ?? []);
  return Object.fromEntries(
    Object.entries(SA_WRITE_CAPABILITIES).map(([right, capability]) => [
      right,
      held.has(capability),
    ]),
  ) as SaWriteRights;
}
